import { useId, type ReactNode, type SelectHTMLAttributes } from 'react'

interface SelectProps<T extends string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'value' | 'onChange' | 'children'> {
  label: ReactNode
  value: T
  onChange: (value: T) => void
  options: readonly { value: T; label: string }[]
  /** Shown under the control and announced with it. */
  hint?: ReactNode
}

/**
 * A labelled `<select>`.
 *
 * ── WHY NOT `SegmentedControl`, AND WHY NOT `ChoiceChips` ───────────────────
 *
 * `ChoiceChips.tsx:24-32` already wrote down half of this: `.segmented` is a
 * flex bar of equal-width options, right for two or three short words and wrong
 * for anything longer. The trend's measures are seven options with names like
 * "Cognitive engagement", which is neither.
 *
 * `ChoiceChips` is wrong for the opposite reason. Its `onChange` is
 * `(value: T | null) => void` because withdrawing an answer is a real state for
 * a journal — and a chart must always be showing something, so a control whose
 * whole point is a way back to unanswered is the wrong shape.
 *
 * A native `<select>` is one 44px row at 360px, and its keyboard behaviour, its
 * accessible name and its platform picker on a phone are the platform's rather
 * than re-implemented and subtly wrong. The markup already existed inline in
 * `Users.tsx`; what it lacked was a generated id, which is why it could not
 * appear twice on one page.
 */
export function Select<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
  className,
  ...rest
}: SelectProps<T>) {
  const id = useId()
  const hintId = `${id}-hint`
  return (
    // `field--select` carries `appearance: none` and the drawn chevron that
    // replaces the platform one it removes -- see `components.css`. The 44px
    // coarse-pointer minimum is NOT new here: `.field__control` always had it,
    // and the recorded WebKit measurement is precisely that the minimum did not
    // hold without `appearance: none`. On the component rather than on a
    // caller's class, so the next `<select>` inherits the fix rather than the
    // bug.
    <div className={['field', 'field--select', className].filter(Boolean).join(' ')}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {/* The chevron hangs off this span rather than off the field, so it is
          centred on the control's own box instead of on an offset measured down
          from the label -- see `.field__select` in `components.css`. */}
      <span className="field__select">
        <select
          {...rest}
          id={id}
          className="field__control"
          value={value}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => onChange(event.target.value as T)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  )
}
